import { Injectable, inject, Type } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DialogConfig } from './dialog.config';
import { DialogService } from './dialog.service';
import { NotificationService } from '../notification.service';
import { TranslateService } from '@ngx-translate/core';
import { DialogComponent } from './dialog-component';

@Injectable({
  providedIn: 'root'
})
export class DialogHelpersService {
  private dialogService = inject(DialogService);
  private notificationService = inject(NotificationService);
  private translate = inject(TranslateService);

  /**
   * Opens a dialog and returns the dialog reference
   */
  open<T extends DialogComponent<D, R>, D, R>(component: Type<T>, config: DialogConfig<D>) {
    return this.dialogService.open<T, D, R>(component, config);
  }

  /**
   * Opens a dialog and handles the result with error handling
   * @param component The dialog component to open
   * @param config Dialog configuration
   * @param onSuccess Callback for successful result
   * @param errorMessage Optional custom error message
   * @returns Promise that resolves to the dialog result or undefined if cancelled/failed
   */
  async openAndHandleResult<
    T extends DialogComponent<D, R>,
    D,
    R extends { setErrorMessage?: (message: string | null) => void; setLoading?: (loading: boolean) => void }
  >(
    component: Type<T>,
    config: DialogConfig<D>,
    onSuccess: (result: R) => Promise<void>,
    errorMessage = this.translate.instant('NOTIFICATIONS.ERROR.ACTION_FAILED')
  ): Promise<R | undefined> {
    try {
      // Open dialog
      const dialogRef = this.dialogService.open<T, D, R>(component, config);

      // Create a promise that resolves when the dialog is actually closed by user or via explicit close() call
      const dialogClosedPromise = firstValueFrom(dialogRef.afterClosed());

      // Listen for dialog submissions
      const subscription = dialogRef.componentInstance?.dialogSubmit.subscribe(async (result: R) => {
        try {
          // Show loading state
          result.setLoading?.(true);

          // Process the submission
          await onSuccess(result);

          // On success, close the dialog
          this.dialogService.close<R, D>(dialogRef, result);
        } catch (error: unknown) {
          result.setLoading?.(false);
          console.error('Error during dialog submission:', error);
          this.dialogService.close<R, D>(dialogRef);
          this.notificationService.error(errorMessage);
        }
      });

      // Wait for dialog to close
      const finalResult = await dialogClosedPromise;

      // Clean up subscription when dialog actually closes
      subscription?.unsubscribe();

      return finalResult;
    } catch (error) {
      console.error('Failed to open dialog:', error);
      this.notificationService.error(this.translate.instant('NOTIFICATIONS.ERROR.FORM_OPEN_FAILURE'));
      return undefined;
    }
  }
}
